import {Icon} from '@wandb/components';
import {toast} from '@wandb/weave/common/components/elements/Toast';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useContext, useEffect} from 'react';

import {useActiveThread} from '../../agentChat/store/context';
import {useDismissibleNotice} from '../../agentChat/useDismissibleNotice';
import {useUnseenSettledThread} from '../../agentChat/useUnseenSettledThread';
import {trackNavRightFeatureOpened} from '../../services/analytics/productEvents';
import {
  useIsAdminModeActiveAndIsNotLocalEnv,
  viewingAs,
} from '../../util/admin';
import {
  AgentChatProvider,
  useAgentChatAvailability,
  useWBAgentController,
} from '../AgentChat';
import {AgentChatRuntimeBoundary} from '../AgentChat/AgentChatProvider';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {ThreadHistoryProvider} from './chatHistory/ThreadHistoryProvider';
import {useTrackWBAgentPanel} from './useTrackWBAgent';
import {WBAgentChatButton} from './WBAgentChatButton';
import {WBAgentChatDrawer} from './WBAgentChatDrawer';
import {WBAgentChatWindow} from './WBAgentChatWindow';
import {useWBAgentContextStore} from './WBAgentContextStore';
import {WBAgentRunDisplayNameProvider} from './WBAgentRunDisplayNameProvider';
import {WBAgentSessionRestore} from './WBAgentSessionRestore';

/**
 * Component tree:
 * WBAgent → WBAgentInner
 *   ├── WBAgentChatButton
 *   ├── WBAgentChatWindow (floating, undocked)
 *   │   ├── WBAgentHeader
 *   │   └── WBAgentThread
 *   │       ├── WBAgentMessageAssistant
 *   │       ├── WBAgentMessageUser
 *   │       └── WBAgentComposer
 *   │           └── WBAgentEditor
 *   └── WBAgentChatDrawer (docked)
 *       ├── WBAgentHeader
 *       └── WBAgentThread
 *           └── ...
 */

export const WBAgent = () => {
  return (
    <AgentChatProvider>
      <WBAgentInner />
    </AgentChatProvider>
  );
};

export const WBAgentInner = () => {
  const {isAvailable: isWorkspaceAgentEnabled} = useAgentChatAvailability();
  const activeThread = useActiveThread();
  const open = useWBAgentContextStore(s => s.open);
  const setOpen = useWBAgentContextStore(s => s.setOpen);
  const docked = useWBAgentContextStore(s => s.docked);
  const {selectedAccount} = useContext(AccountSelectorContext);
  const isAdminMode =
    useIsAdminModeActiveAndIsNotLocalEnv() || viewingAs() !== '';
  const {dismissed: introDismissed, dismiss: dismissIntro} =
    useDismissibleNotice('wbagent-intro');
  const {trackOpened, trackClosed, trackNavbarButtonViewed} =
    useTrackWBAgentPanel();
  const {openChatToThread} = useWBAgentController();

  // Handles notifying user via toast that a chat completed while the agent was minimized.
  // The callback only fires for live completions --> the toast won't re-appear
  // when the persisted value is rehydrated after a page reload.
  const unseenSettledThread = useUnseenSettledThread(open, thread => {
    toast(
      // Use a Tailwind wrapper here because toasts render in react-toastify's
      // container (mounted near the app root), outside any `.tw-style` ancestor
      <TailwindContents>
        <div className="flex items-center text-base gap-16">
          <Icon icon="checkmark2" size="sm" />
          New response
          <button
            type="button"
            onClick={() => openChatToThread(thread.rootTurnId)}
            className="ml-auto mr-16 cursor-pointer appearance-none border-0 bg-transparent p-0 font-semibold text-teal-400">
            Go to chat
          </button>
        </div>
      </TailwindContents>,
      {type: 'success'}
    );
  });

  // If the chat is already open on mount, the user has discovered it — skip the cue.
  useEffect(() => {
    if (open) {
      dismissIntro();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {isWorkspaceAgentEnabled && (
        <WBAgentChatButton
          showOnboardingCue={!introDismissed}
          unseenSettledThread={unseenSettledThread}
          onImpression={trackNavbarButtonViewed}
          onPress={() => {
            if (!open) {
              trackNavRightFeatureOpened({
                feature: 'agent',
                selected_account: selectedAccount,
                is_admin_mode: isAdminMode,
              });
              trackOpened();
            } else {
              trackClosed();
            }
            if (!introDismissed) {
              dismissIntro();
            }
            setOpen(!open);
          }}
          isLoading={activeThread?.status === 'running'}
        />
      )}
      <AgentChatRuntimeBoundary>
        <WBAgentSessionRestore />
        {open && (
          <WBAgentRunDisplayNameProvider>
            <ThreadHistoryProvider>
              {docked ? <WBAgentChatDrawer /> : <WBAgentChatWindow />}
            </ThreadHistoryProvider>
          </WBAgentRunDisplayNameProvider>
        )}
      </AgentChatRuntimeBoundary>
    </>
  );
};
